export const HIGHLIGHT_URL_ID_CAP = 100;

/** Parse the `highlight` query param into a de-duplicated list of file ids. */
export function parseHighlightIds(raw: string | null | undefined): string[] {
  if (!raw) return [];
  const ids = raw.split(',').map((s) => s.trim()).filter(Boolean);
  return Array.from(new Set(ids));
}

/** Build `/file-storage?highlight=a,b,...` (capped). Returns the bare path when no ids. */
export function buildFileStorageHighlightUrl(ids: string[], cap: number = HIGHLIGHT_URL_ID_CAP): string {
  const unique = Array.from(new Set(ids.map((s) => s.trim()).filter(Boolean)));
  if (unique.length === 0) return '/file-storage';
  return `/file-storage?highlight=${encodeURIComponent(unique.slice(0, cap).join(','))}`;
}

/** Ancestor folder paths for a file's folder (e.g. "A/B" -> ["A", "A/B"]). */
export function folderAncestorPaths(folder: string | null | undefined): string[] {
  const parts = (folder || 'Other').split('/');
  const paths: string[] = [];
  let acc = '';
  for (const p of parts) {
    acc = acc ? `${acc}/${p}` : p;
    paths.push(acc);
  }
  return paths;
}

export const BATCH_HIGHLIGHT_KEY = 'batchHighlightFileIds';

export interface ImportResultLike {
  needs_direction_review?: boolean;
  company_not_detected?: boolean;
  needs_review?: boolean;
  parse_failed?: boolean;
  empty_transactions?: boolean;
}

/** True when the OCR result carries any review flag (mirrors FileUpload.tsx's needsReview). */
export function needsReview(result: ImportResultLike | null | undefined): boolean {
  return !!(
    result?.needs_direction_review ||
    result?.company_not_detected ||
    result?.needs_review ||
    result?.parse_failed ||
    result?.empty_transactions
  );
}

// ── Session review-queue machinery — OBSOLETE 2026-11-04 ──
// The post-upload one-by-one review carousel was retired in favour of the
// checklist-centric flow: uploads land on /file-upload/review (postUploadPlan)
// and each file is reviewed from there (Review Upload → detail → back; the
// review pages' exitDest `from` markers handle the return). NOTHING writes the
// 'reviewQueue' sessionStorage key anymore, so advanceReviewQueue always takes
// its no-queue path. The functions below are kept (tests still pin their
// contracts) for revival knowledge — do not add new callers.
export interface QueueEntry {
  docType: string;
  reviewId: string;
  filename: string;
  flags?: string;
}

/** Route for a queued review item. */
export function reviewItemUrl(entry: QueueEntry): string {
  if (entry.docType === 'bank_statement') return `/bank-statements/review/${entry.reviewId}${entry.flags || ''}`;
  if (entry.docType === 'card_statement') return `/card-statements/review/${entry.reviewId}${entry.flags || ''}`;
  return `/invoices/review/${entry.reviewId}${entry.flags || ''}`;
}

export interface DuplicateResultLike {
  is_duplicate?: boolean;
  duplicate_blocked?: boolean;
  duplicate_status?: string | null;
  statement_id?: string | null;
  invoice_id?: string | null;
}

/**
 * Review route for a duplicate import — the existing record's review page with
 * the duplicate-notice query flags (`is_duplicate`, `dup_blocked`, `dup_status`).
 * Returns null when the result is not a duplicate or carries no record id, so
 * the caller can fall back to its normal routing.
 *
 * The API reports content-hash duplicates as a 201 success with
 * `is_duplicate: true` (never 409) so no second statement/JE is created; without
 * this the re-uploaded file is left unlinked and shows the grey "Stored" badge
 * instead of the duplicate notice.
 */
export function duplicateNoticePath(
  result: DuplicateResultLike | null | undefined,
  docType: string,
): string | null {
  if (!result?.is_duplicate) return null;
  const id = docType === 'invoice' ? result.invoice_id : result.statement_id;
  if (!id) return null;
  const base = docType === 'bank_statement' ? 'bank-statements'
    : docType === 'card_statement' ? 'card-statements'
      : docType === 'invoice' ? 'invoices'
        : null;
  if (!base) return null;
  const params = new URLSearchParams();
  params.set('is_duplicate', '1');
  if (result.duplicate_blocked) params.set('dup_blocked', '1');
  if (result.duplicate_status) params.set('dup_status', result.duplicate_status);
  return `/${base}/review/${id}?${params.toString()}`;
}

/**
 * Notice route for the exact case the API *blocks*: re-uploading a bank
 * statement whose bytes already exist as a live statement. Returns null for
 * anything else, so other types keep their normal routing.
 */
export function bankDuplicateNoticePath(
  result: (DuplicateResultLike & { type?: string | null }) | null | undefined,
): string | null {
  if (result?.duplicate_blocked !== true) return null;
  if (result.type && result.type !== 'bank_statement') return null;
  return duplicateNoticePath(result, 'bank_statement');
}

/** Persist the ids to highlight once the review queue is cleared. */
export function stashBatchHighlightIds(ids: string[]): void {
  try {
    sessionStorage.setItem(BATCH_HIGHLIGHT_KEY, JSON.stringify(ids));
  } catch { /* sessionStorage unavailable */ }
}

function readBatchHighlightIds(): string[] {
  try {
    const raw = sessionStorage.getItem(BATCH_HIGHLIGHT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/** Read the stashed ids without clearing them. */
export function peekBatchHighlightIds(): string[] {
  return readBatchHighlightIds();
}

/** Read and clear the stashed ids. */
export function takeBatchHighlightIds(): string[] {
  const ids = readBatchHighlightIds();
  try { sessionStorage.removeItem(BATCH_HIGHLIGHT_KEY); } catch { /* ignore */ }
  return ids;
}

/** LEGACY 2026-11-05: the Review Upload subpage was merged into Pending
 *  Review (/review-queue) — the /file-upload/review route redirects there.
 *  Kept for old deep links and the exitDest legacy marker. */
export const UPLOAD_REVIEW_URL = '/file-upload/review';

/**
 * Decide where an upload flow goes after processing (2026-11-05): the batch
 * STAYS on the Upload page — its stage machine takes over (scanning while OCR
 * jobs run, then the Upload Complete state with next-step buttons). Only the
 * highlight stash remains: imported ids stay stashed when any file needs
 * review, so the review pages' batch handoff still ends on the File Storage
 * highlight once everything is confirmed.
 */
export function postUploadPlan(importedFileIds: string[], queued: number): { stay: boolean; stash: string[] } {
  return { stay: true, stash: queued > 0 ? importedFileIds : [] };
}

/**
 * Plan what happens when a batch upload finishes.
 * - queued  > 0 -> mixed batch: stash the ids for the review-queue handoff.
 * - queued == 0 -> all-clean: navigate straight to the highlighted files, but only
 *   while the user is still on File Storage (never yank them back).
 */
export function postBatchPlan(
  fileIds: string[],
  queued: number,
  onFileStorage: boolean,
): { navigate?: string; stash?: string[] } {
  if (queued > 0) return { stash: fileIds };
  if (fileIds.length > 0 && onFileStorage) return { navigate: buildFileStorageHighlightUrl(fileIds) };
  return {};
}

export interface QueueHandoff {
  nextUrl?: string;
  highlightUrl?: string;
}

/**
 * Advance the review queue after confirming/discarding the current item and decide
 * where to go next.
 * - queue still has a next item -> persist the shortened queue, return its review URL,
 *   and leave the batch-highlight stash for later.
 * - queue absent or just drained -> clear the queue keys, consume the stashed batch ids
 *   (even when the queue was cleared elsewhere), and return the File Storage highlight URL.
 *
 * Returns null when there is nowhere to go.
 */
export function advanceReviewQueue(currentReviewId?: string): QueueHandoff | null {
  let queue: QueueEntry[] | null = null;
  try {
    const raw = sessionStorage.getItem('reviewQueue');
    const parsed = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed)) queue = parsed;
  } catch { /* malformed/absent queue -> drain path below */ }

  // Contract: queue[0] is the review the user is currently ON. When the caller
  // passes its id and the head doesn't match, this queue belongs to a DIFFERENT
  // (stale) session — e.g. leftovers from an earlier upload whose records were
  // since deleted. Consuming it would navigate the user to a dead review page,
  // so touch nothing and report "no queue handoff".
  if (currentReviewId !== undefined && (!queue || !queue[0] || queue[0].reviewId !== currentReviewId)) {
    return null;
  }

  if (queue && queue.length > 0) {
    queue.shift();
    if (queue.length > 0) {
      try { sessionStorage.setItem('reviewQueue', JSON.stringify(queue)); } catch { /* ignore */ }
      return { nextUrl: reviewItemUrl(queue[0]) };
    }
  }
  try {
    sessionStorage.removeItem('reviewQueue');
    sessionStorage.removeItem('reviewQueueTotal');
  } catch { /* ignore */ }
  const batch = takeBatchHighlightIds();
  if (batch.length > 0) return { highlightUrl: buildFileStorageHighlightUrl(batch) };
  return null;
}

/** Marker param appended by File Storage when it navigates to a review page. */
export const FROM_FILE_STORAGE = 'file-storage';
/** Marker param appended by the Review-upload checklist when it opens a review
 *  (server-built review_url) — finished reviews land back on the checklist
 *  instead of the record's list page. LEGACY 2026-11-05: the Review Upload page
 *  was merged into Pending Review; the marker lingers only in old links and
 *  /upload-review's server-built URLs, and now returns to Pending Review. */
export const FROM_UPLOAD_REVIEW = 'file-upload-review';
/** Marker param appended by the merged Pending Review page's server-built
 *  review URLs — finished reviews land back on /review-queue. */
export const FROM_REVIEW_QUEUE = 'review-queue';

/**
 * Append `from=file-storage` to a review-page URL so save/discard/review-later
 * can land back on File Storage instead of the record's list page.
 */
export function withFromParam(url: string): string {
  if (!url) return url;
  return url.includes('?') ? `${url}&from=${FROM_FILE_STORAGE}` : `${url}?from=${FROM_FILE_STORAGE}`;
}

/**
 * Where a finished review should land when there is no queued next item:
 * back to the page the review was entered from (`from` param — File Storage,
 * Pending Review, or the legacy Review-upload checklist), otherwise the page's
 * own default (e.g. /ar, /bank-statements).
 */
export function exitDest(fromParam: string | null | undefined, defaultDest: string): string {
  if (fromParam === FROM_FILE_STORAGE) return '/file-storage';
  if (fromParam === FROM_REVIEW_QUEUE) return '/review-queue';
  if (fromParam === FROM_UPLOAD_REVIEW) return '/review-queue';
  return defaultDest;
}
